// Bilingue anglais / français.
//
// L'anglais est la langue par défaut : le produit s'adresse d'abord à un
// marché anglophone, et un visiteur francophone bascule en un clic. Le
// choix est mémorisé dans localStorage, donc il survit au rechargement et
// à la navigation entre le site vitrine et l'espace client.
//
// Un dictionnaire n'est PAS une liste de clés plates : c'est un objet
// imbriqué qu'on lit directement en JSX (`t.hero.title`). Les chaînes qui
// dépendent d'un nombre ou d'une valeur sont des fonctions — c'est la seule
// façon honnête de gérer un pluriel, les règles n'étant pas les mêmes d'une
// langue à l'autre.
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import en from '../locales/en'
import fr from '../locales/fr'

const DICTS = { en, fr }

// L'ordre de ce tableau est celui du sélecteur de langue.
export const LOCALES = [
  { v: 'en', label: 'EN', full: 'English', tag: 'en-US' },
  { v: 'fr', label: 'FR', full: 'Français', tag: 'fr-FR' }
]

export const DEFAULT_LANG = 'en'
const STORAGE_KEY = 'ctrl_lang'

function readStored () {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return DICTS[v] ? v : DEFAULT_LANG
  } catch {
    // navigateur en navigation privée stricte, stockage refusé
    return DEFAULT_LANG
  }
}

/**
 * Fusion profonde : le dictionnaire anglais sert de socle, la langue
 * choisie l'écrase. Une clé oubliée dans une traduction affiche l'anglais
 * plutôt que `undefined` — une phrase dans la mauvaise langue reste
 * infiniment moins grave qu'une page blanche.
 */

/**
 * Un « rayon » du dictionnaire, dans lequel on a le droit de descendre.
 *
 * Tout le reste — chaîne, fonction, tableau, et SURTOUT élément React —
 * se remplace en bloc. Recopier un élément React clé par clé produirait un
 * objet qui lui ressemble sans en être un : les fragments `<>…</>` des
 * dictionnaires reviendraient cassés, et l'erreur serait très pénible à
 * relier à cette ligne.
 */
function isBranch (v) {
  return typeof v === 'object' && v !== null &&
    !Array.isArray(v) && v.$$typeof === undefined
}

function merge (base, over) {
  if (over === undefined || over === null) return base
  if (!isBranch(base) || !isBranch(over)) return over

  const out = { ...base }
  for (const k of Object.keys(over)) out[k] = merge(base[k], over[k])
  return out
}

const LangContext = createContext(null)

export function LangProvider ({ children }) {
  const [lang, setLangState] = useState(readStored)

  // L'attribut lang de <html> n'est pas cosmétique : il pilote la coupure
  // des mots, les guillemets typographiques et surtout les lecteurs
  // d'écran, qui prononceraient un texte anglais avec une voix française.
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const value = useMemo(() => {
    const entry = LOCALES.find(l => l.v === lang) || LOCALES[0]
    return {
      lang,
      locale: entry.tag,
      setLang: next => {
        if (!DICTS[next]) return
        try { localStorage.setItem(STORAGE_KEY, next) } catch { /* stockage refusé */ }
        setLangState(next)
      },
      t: lang === 'en' ? en : merge(en, DICTS[lang])
    }
  }, [lang])

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>
}

export function useLang () {
  const ctx = useContext(LangContext)
  if (!ctx) throw new Error('useLang doit être utilisé dans <LangProvider>')
  return ctx
}

/** Raccourci pour le cas courant : on ne veut que les textes. */
export function useT () {
  return useLang().t
}

/**
 * Le dictionnaire courant HORS composant React.
 *
 * Réservé aux modules qui n'ont pas de hooks à disposition — en pratique le
 * client HTTP, qui doit formuler « impossible de joindre le serveur » sans
 * être un composant. Ne pas l'utiliser dans du JSX : il ne provoque aucun
 * rendu quand la langue change.
 */
export function getDict () {
  const lang = readStored()
  return lang === 'en' ? en : merge(en, DICTS[lang])
}

/**
 * Formatage de date dans la langue courante.
 * `value` accepte un ISO SQL (« 2026-07-28 21:03:00 ») ou une Date.
 */
export function formatDate (value, locale, opts) {
  if (!value) return '—'
  const d = value instanceof Date ? value : new Date(String(value).replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(locale, opts)
}
